<template>
    <div class="edit-address">
        <!-- Address Section -->
        <div class="edit-address__main">
            <div class="edit-address__main-header section-box-header">
                <h3 class="edit-address__main-title section-box-title">Address</h3>
            </div>
            <themed-form
                :fields="addressFields"
                :model-value="building"
                layout="grid"
                :columns="4"
                @input="updateBuilding"
            />
        </div>

        <!-- Geolocation Section - Full Width Below -->
        <div class="edit-address__geolocation">
            <div class="edit-address__geolocation-header section-box-header">
                <h3 class="edit-address__geolocation-title section-box-title">Geolocation</h3>
            </div>
            <themed-form
                :fields="geolocationFields"
                :model-value="building"
                layout="grid"
                :columns="3"
                @input="updateBuilding"
            />
        </div>
    </div>
</template>

<script>
    import ThemedForm from '../../form/ThemedForm.vue';

    export default {
        name: 'EditAddress',

        components: {
            ThemedForm
        },

        props: {
            buildingData: {
                type: Object,
                required: true
            }
        },

        data() {
            return {
                building: this.buildingData,
                states: [],
                countries: [],
                availableRegions: [],
                addressFieldsData: [],
                geolocationFieldsData: []
            }
        },

        watch: {
            countries() {
                this.updateAddressFields();
            },
            states() {
                this.updateAddressFields();
            },
            regions() {
                this.updateGeolocationFields();
            }
        },

        mounted() {
            this.initAddressInfo();
        },

        computed: {
            regions() {
                return _.filter(this.availableRegions, ['country_id', this.building.country_id]);
            },

            countryOptions() {
                return this.countries.map(country => ({
                    value: country.id,
                    label: country.name
                }));
            },

            stateOptions() {
                return this.states.map(state => ({
                    value: state.state,
                    label: state.state
                }));
            },

            regionOptions() {
                return this.regions.map(region => ({
                    value: region.id,
                    label: region.name
                }));
            },

            addressFields() {
                // Return data property that gets updated by watchers
                return this.addressFieldsData.length > 0 ? this.addressFieldsData : this.getAddressFieldsConfig();
            },

            geolocationFields() {
                // Return data property that gets updated by watchers
                return this.geolocationFieldsData.length > 0 ? this.geolocationFieldsData : this.getGeolocationFieldsConfig();
            }
        },

        methods: {
            getAddressFieldsConfig() {
                const countryOpts = this.countryOptions;
                const stateOpts = this.stateOptions;
                
                return [
                    {
                        name: 'country_id',
                        type: 'select',
                        label: 'Country / Region',
                        col: 1,
                        options: countryOpts,
                        componentProps: {
                            placeholder: 'Select country'
                        }
                    },
                    {
                        name: 'building_number',
                        type: 'input',
                        label: 'Building #',
                        col: 1,
                        componentProps: {
                            placeholder: 'Building number'
                        }
                    },
                    {
                        name: 'building_name',
                        type: 'input',
                        label: 'Building Name',
                        col: 2,
                        componentProps: {
                            placeholder: 'Building name'
                        }
                    },
                    {
                        name: 'address_line_1',
                        type: 'input',
                        label: 'Address Line 1',
                        col: 2,
                        componentProps: {
                            placeholder: 'Street address'
                        }
                    },
                    {
                        name: 'address_line_2',
                        type: 'input',
                        label: 'Address Line 2',
                        col: 2,
                        componentProps: {
                            placeholder: 'Apartment, suite, etc.'
                        }
                    },
                    {
                        name: 'city',
                        type: 'input',
                        label: 'City',
                        col: 1,
                        componentProps: {
                            placeholder: 'City'
                        }
                    },
                    {
                        name: 'state',
                        type: 'select',
                        label: 'State',
                        col: 1,
                        conditionalFn: (data) => data.country_id == 16,
                        options: stateOpts,
                        componentProps: {
                            placeholder: 'Select state'
                        }
                    },
                    {
                        name: 'postal_code',
                        type: 'input',
                        label: 'ZIP / Postal',
                        col: 1,
                        componentProps: {
                            placeholder: 'Postal code'
                        }
                    },
                    {
                        name: 'county',
                        type: 'input',
                        label: 'County Name',
                        col: 1,
                        conditionalFn: (data) => data.country_id == 16,
                        componentProps: {
                            placeholder: 'County'
                        }
                    }
                ];
            },

            getGeolocationFieldsConfig() {
                const regionOpts = this.regionOptions;
                
                return [
                    {
                        name: 'region_id',
                        type: 'select',
                        label: 'Region',
                        col: 1,
                        conditionalFn: () => this.regions.length > 0,
                        options: regionOpts,
                        componentProps: {
                            placeholder: 'Select region'
                        }
                    },
                    {
                        name: 'latitude',
                        type: 'input',
                        label: 'Latitude',
                        col: 1,
                        componentProps: {
                            type: 'number',
                            step: 'any',
                            placeholder: 'Latitude'
                        }
                    },
                    {
                        name: 'longitude',
                        type: 'input',
                        label: 'Longitude',
                        col: 1,
                        componentProps: {
                            type: 'number',
                            step: 'any',
                            placeholder: 'Longitude'
                        }
                    }
                ];
            },

            updateAddressFields() {
                this.addressFieldsData = this.getAddressFieldsConfig();
            },

            updateGeolocationFields() {
                this.geolocationFieldsData = this.getGeolocationFieldsConfig();
            },

            initAddressInfo() {
                axios.get('/api/address-info')
                    .then((response) => {
                        this.states = response.data.states;
                        this.countries = response.data.countries;
                        this.availableRegions = response.data.regions;
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            },

            updateBuilding(updatedData) {
                Object.assign(this.building, updatedData);
            }
        }
    }
</script>
